import { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";
// ========================
// Design tokens
// ========================
// A warmer, more organic palette than a plain "notebook": soft cream
// paper, warm ink-brown text, sage as the calm working colour, and
// terracotta as the one warm accent that gets to be a little louder
// (buttons, highlights, the mood the whole app leans on for "alive").
const palette = {
  paper: "#F5EAD2",
  paperDeep: "#ECDFC1",
  paperLine: "rgba(59, 50, 39, 0.14)",
  cardBg: "#FBF4E4",
  cardGlass: "rgba(251, 244, 228, 0.72)",
  ink: "#3B3227",
  inkSoft: "#6B5F4F",
  inkFaint: "#9A8D78",
  indigo: "#4B7A50",       // primary working colour (vivid sage)
  indigoLight: "#6FA06B",  // sage, lighter
  brass: "#E07A3F",        // primary accent (vivid terracotta/orange)
  brassLight: "#F2A25F",
  wine: "#D1483A",
  wineLight: "#E37364",
  sage: "#4B7A50",
};

const brassGradient = `linear-gradient(135deg, ${palette.brass} 0%, ${palette.brassLight} 100%)`;
const sageGradient = `linear-gradient(135deg, ${palette.indigo} 0%, ${palette.indigoLight} 100%)`;
const cardShadow = "0 10px 30px rgba(59,50,39,0.10), 0 2px 8px rgba(59,50,39,0.06)";
const glassBorder = "1px solid rgba(255,255,255,0.5)";

const appBackground = `
  radial-gradient(circle at 10% 0%, rgba(75, 122, 80, 0.08), transparent 45%),
  radial-gradient(circle at 92% 6%, rgba(224, 122, 63, 0.10), transparent 42%),
  ${"#F5EAD2"}
`;

const fontSerif = '"Iowan Old Style", Georgia, "Times New Roman", serif';
const fontSans = '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif';

// A small quill mark used instead of a boxed letter — the brand should
// read as "written", not as an app-icon tile.
function BrandMark({ size = 26, color = palette.brass }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      <path
        d="M19.5 4.5c-4.2 0-8.6 2.1-11.3 6.1-1.6 2.4-2.4 5-2.6 7.3 2.3-.4 4.7-1.4 6.9-3.1 3.6-2.8 5.5-6.6 7-10.3z"
        fill={color}
        opacity="0.9"
      />
      <path d="M5.6 18.9 12.4 11" stroke={color} strokeWidth="1.4" strokeLinecap="round" opacity="0.55" />
    </svg>
  );
}

// Puts the gradient background on <body> itself, so it covers the whole
// viewport and nothing (margin collapsing, page edges) can reveal a flat
// strip of a different colour.
function GlobalStyle() {
  return (
    <style>{`
      html, body, #root { margin: 0; padding: 0; min-height: 100%; }
      body { background: ${appBackground}; background-attachment: fixed; }
      * { box-sizing: border-box; }
    `}</style>
  );
}

// ========================
// Styles
// ========================
const styles = {
  container: {
    minHeight: "100vh",
    fontFamily: fontSans,
    color: palette.ink,
  },
  header: {
    background: palette.paper,
    padding: "20px 40px",
    color: palette.ink,
    borderBottom: `1px solid ${palette.paperLine}`,
  },
  headerContent: {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "14px",
  },
  logoRow: { display: "flex", alignItems: "center", gap: "10px" },
  logo: { fontSize: "19px", fontWeight: "600", fontFamily: fontSerif, letterSpacing: "0.2px", color: palette.ink },
  nav: { display: "flex", gap: "4px", alignItems: "center", flexWrap: "wrap" },
  navButton: {
    background: "transparent",
    border: "none",
    borderBottom: "2px solid transparent",
    padding: "8px 12px",
    color: palette.inkSoft,
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14.5px",
    transition: "color 0.2s ease, border-color 0.2s ease",
  },
  navButtonActive: {
    color: palette.ink,
    borderBottom: `2px solid ${palette.brass}`,
  },
  headerCta: {
    background: brassGradient,
    color: palette.paper,
    border: "none",
    borderRadius: "999px",
    padding: "10px 20px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 6px 16px rgba(224,122,63,0.35)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    marginLeft: "8px",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "48px 24px 80px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))",
    gap: "24px",
    marginTop: "24px",
  },
  card: {
    background: palette.cardGlass,
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderRadius: "16px",
    padding: "28px",
    border: glassBorder,
    boxShadow: cardShadow,
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
  },
  infoCard: {
    background: sageGradient,
    color: palette.paper,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    border: `1px solid ${palette.indigo}`,
  },
  // Vertical centring is done by the flex wrapper in AuthPage, so the
  // card itself has no vertical margin (which used to collapse outwards
  // and expose a flat strip above the card).
  authCard: {
    width: "100%",
    maxWidth: "440px",
    margin: "0 auto",
    background: palette.cardGlass,
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    borderRadius: "20px",
    padding: "44px 40px",
    border: glassBorder,
    boxShadow: "0 20px 50px rgba(59,50,39,0.16), 0 4px 14px rgba(59,50,39,0.08)",
  },
  authWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "40px 16px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "600",
    marginBottom: "20px",
    color: palette.ink,
    fontFamily: fontSerif,
  },
  textarea: {
    width: "100%",
    minHeight: "180px",
    padding: "16px",
    borderRadius: "10px",
    border: `1px solid ${palette.paperLine}`,
    fontSize: "16px",
    lineHeight: "1.6",
    fontFamily: "inherit",
    resize: "vertical",
    backgroundColor: palette.paperDeep,
    outline: "none",
    boxSizing: "border-box",
    color: palette.ink,
  },
  input: {
    width: "100%",
    padding: "13px 14px",
    borderRadius: "10px",
    border: `1px solid ${palette.paperLine}`,
    fontSize: "15px",
    marginTop: "14px",
    backgroundColor: palette.paperDeep,
    outline: "none",
    boxSizing: "border-box",
    color: palette.ink,
    fontFamily: "inherit",
  },
  button: {
    width: "100%",
    padding: "14px",
    marginTop: "20px",
    background: brassGradient,
    color: palette.paper,
    border: "none",
    borderRadius: "999px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(224,122,63,0.38)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
  secondaryButton: {
    width: "100%",
    padding: "14px",
    marginTop: "12px",
    background: "rgba(255,255,255,0.4)",
    color: palette.ink,
    border: `1px solid ${palette.paperLine}`,
    borderRadius: "999px",
    fontSize: "16px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "background 0.2s ease, transform 0.2s ease",
  },
  result: {
    marginTop: "20px",
    padding: "22px",
    borderRadius: "14px",
    background: sageGradient,
    color: palette.paper,
    boxShadow: "0 14px 30px rgba(75,122,80,0.25)",
    borderLeft: `4px solid ${palette.brass}`,
  },
  entryCard: {
    background: palette.cardGlass,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    borderRadius: "14px",
    padding: "22px",
    marginBottom: "14px",
    border: glassBorder,
    boxShadow: "0 8px 22px rgba(59,50,39,0.08)",
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    position: "relative",
  },
  dateHeader: {
    fontSize: "17px",
    fontWeight: "600",
    color: palette.ink,
    marginTop: "28px",
    marginBottom: "12px",
    fontFamily: fontSerif,
    paddingBottom: "6px",
    borderBottom: `1px solid ${palette.paperLine}`,
  },
  chip: {
    display: "inline-block",
    padding: "7px 15px",
    background: "rgba(241, 234, 217, 0.12)",
    borderRadius: "999px",
    marginRight: "10px",
    marginTop: "10px",
    fontSize: "13.5px",
    fontWeight: "500",
    border: "1px solid rgba(241, 234, 217, 0.3)",
  },
  errorMessage: {
    marginTop: "14px",
    padding: "12px 14px",
    borderRadius: "8px",
    background: "rgba(123, 59, 70, 0.08)",
    color: palette.wine,
    fontSize: "14px",
    borderLeft: `3px solid ${palette.wine}`,
  },
  successMessage: {
    marginTop: "14px",
    padding: "12px 14px",
    borderRadius: "8px",
    background: "rgba(85, 112, 92, 0.08)",
    color: palette.sage,
    fontSize: "14px",
    borderLeft: `3px solid ${palette.sage}`,
  },
  deleteButton: {
    position: "absolute",
    top: "18px",
    right: "18px",
    background: "transparent",
    border: `1px solid ${palette.paperLine}`,
    borderRadius: "8px",
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: "13px",
    color: palette.inkSoft,
    fontWeight: "500",
  },
  statCard: {
    background: palette.cardGlass,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    borderRadius: "16px",
    padding: "24px",
    border: glassBorder,
    boxShadow: cardShadow,
    transition: "transform 0.25s ease, box-shadow 0.25s ease",
    textAlign: "left",
  },
  statNumber: {
    fontSize: "36px",
    fontWeight: "700",
    backgroundImage: sageGradient,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    fontFamily: fontSerif,
    marginBottom: "6px",
  },
  statLabel: {
    fontSize: "13px",
    color: palette.inkFaint,
    fontWeight: "600",
  },
  emotionBar: {
    display: "flex",
    alignItems: "center",
    marginBottom: "12px",
    gap: "12px",
  },
  emotionBarFill: {
    height: "24px",
    borderRadius: "8px",
    boxShadow: "0 3px 8px rgba(59,50,39,0.15)",
    transition: "width 0.5s ease",
    minWidth: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingRight: "10px",
    color: palette.paper,
    fontSize: "12px",
    fontWeight: "700",
  },
  helperText: {
    fontSize: "13px",
    color: palette.inkFaint,
    marginTop: "8px",
    lineHeight: "1.5",
  },
  promptGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: "10px",
    marginTop: "14px",
  },
  promptButton: {
    border: `1px solid ${palette.paperLine}`,
    background: palette.paperDeep,
    color: palette.inkSoft,
    borderRadius: "8px",
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "13.5px",
    textAlign: "left",
  },
  filterPanel: {
    background: palette.cardGlass,
    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",
    borderRadius: "16px",
    padding: "20px",
    marginBottom: "22px",
    border: glassBorder,
    boxShadow: cardShadow,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: "14px",
    alignItems: "end",
  },
  select: {
    width: "100%",
    padding: "11px 13px",
    borderRadius: "8px",
    border: `1px solid ${palette.paperLine}`,
    fontSize: "14.5px",
    backgroundColor: palette.paperDeep,
    outline: "none",
    boxSizing: "border-box",
    color: palette.ink,
  },
  compactButton: {
    padding: "12px 20px",
    background: brassGradient,
    color: palette.paper,
    border: "none",
    borderRadius: "999px",
    fontSize: "14.5px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 8px 18px rgba(224,122,63,0.32)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
  progressTrack: {
    width: "100%",
    height: "6px",
    borderRadius: "999px",
    background: palette.paperLine,
    overflow: "hidden",
    marginTop: "10px",
  },
  progressFill: {
    height: "100%",
    borderRadius: "999px",
    transition: "width 0.4s ease",
  },
  insightGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
    marginBottom: "30px",
  },
  dashboardHero: {
    background: sageGradient,
    color: palette.paper,
    borderRadius: "20px",
    padding: "34px",
    marginBottom: "26px",
    border: "1px solid rgba(255,255,255,0.25)",
    boxShadow: "0 18px 40px rgba(75,122,80,0.28)",
  },
  dashboardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
    marginBottom: "26px",
  },
  infoTag: {
    position: "absolute",
    top: "14px",
    right: "14px",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    color: palette.inkFaint,
    fontWeight: "600",
    fontSize: "12px",
    border: `1px solid ${palette.paperLine}`,
    cursor: "help",
  },
  moodDot: {
    display: "inline-block",
    width: "9px",
    height: "9px",
    borderRadius: "50%",
    marginRight: "8px",
    flexShrink: 0,
  },
  moodBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 14px",
    borderRadius: "999px",
    fontWeight: "600",
    fontSize: "13.5px",
  },
};

// ========================
// Emotion palette — a quiet colour and a small mark, not a cartoon face
// ========================
const emotionColors = {
  "радість": "#E0A430",
  "смуток": "#4A87BD",
  "злість": "#D1483A",
  "страх": "#8A6FC2",
  "любов": "#E0637F",
  "здивування": "#2FA99A",
  "відраза": "#8FA23F",
  "нейтральний стан": "#A79A82",
};
const emotionFallbackColor = palette.inkFaint;

const writingPrompts = [
  "Сьогодні я відчула/відчув...",
  "Найважливіша подія дня — ...",
  "Мені допомогло заспокоїтись...",
  "Завтра я хочу спробувати...",
];

const normalizeConfidence = (value) => Math.max(0, Math.min(1, Number(value) || 0));

const parseTags = (tags) => String(tags || "")
  .split(",")
  .map((tag) => tag.trim())
  .filter(Boolean);

const getIndexColor = (index) => {
  if (index <= -60) return palette.wine;
  if (index <= -20) return palette.brass;
  if (index < 20) return palette.inkFaint;
  if (index < 60) return palette.indigoLight;
  return palette.sage;
};

const getIndexLabel = (index) => {
  if (index <= -60) return "Виражено негативний";
  if (index <= -20) return "Переважно негативний";
  if (index < 20) return "Переважно нейтральний";
  if (index < 60) return "Переважно позитивний";
  return "Виражено позитивний";
};

const getIndexDescription = (index) => {
  if (index <= -60) return "Емоційний фон записів має виражено негативну спрямованість.";
  if (index <= -20) return "Емоційний фон записів є переважно негативним.";
  if (index < 20) return "Емоційний фон записів близький до нейтрального.";
  if (index < 60) return "Емоційний фон записів має переважно позитивну спрямованість.";
  return "Емоційний фон записів має виражено позитивну спрямованість.";
};

const getValenceTooltip = (index, periodLabel) => {
  const status = getIndexLabel(index);
  return `${periodLabel}: ${status}. Індекс валентності — кількісний показник переважання позитивної або негативної емоційної спрямованості в записах. Значення від -100 до +100. Поточне значення: ${index}.`;
};

const downloadCsv = (entries) => {
  const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const rows = [
    ["date", "emotion", "confidence", "tags", "text"],
    ...entries.map((entry) => [
      entry.date,
      entry.emotion,
      `${(normalizeConfidence(entry.confidence) * 100).toFixed(1)}%`,
      entry.tags,
      entry.text,
    ]),
  ];
  const csv = rows.map((row) => row.map(escapeCsv).join(",")).join("\n");
  const blob = new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `emotion-diary-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Small, quiet mood indicator: a colour mark plus the emotion's own name.
// Used everywhere an emoji-face used to stand in for the emotion.
function Mood({ emotion, size = "normal" }) {
  const color = emotionColors[emotion] || emotionFallbackColor;
  if (size === "badge") {
    return (
      <span style={{ ...styles.moodBadge, background: `${color}1c`, color }}>
        <span style={{ ...styles.moodDot, background: color }} />
        {emotion || "Невідомо"}
      </span>
    );
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center" }}>
      <span style={{ ...styles.moodDot, background: color }} />
      {emotion || "—"}
    </span>
  );
}


// ========================
// Auth Page
// ========================
function AuthPage({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!username || !password) {
      setError("Заповніть всі поля");
      return;
    }

    if (!isLogin && password !== confirmPassword) {
      setError("Паролі не співпадають");
      return;
    }

    if (password.length < 4) {
      setError("Пароль повинен містити мінімум 4 символи");
      return;
    }

    setLoading(true);

    try {
      const endpoint = isLogin ? "/login" : "/register";
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const data = await res.json();

      if (res.ok) {
        if (isLogin) {
          setSuccess("Успішний вхід.");
          localStorage.setItem("token", data.access_token);
          localStorage.setItem("username", data.username);
          setTimeout(() => onLogin(data.username, data.access_token), 800);
        } else {
          setSuccess("Реєстрація успішна. Тепер увійдіть.");
          setTimeout(() => {
            setIsLogin(true);
            setPassword("");
            setConfirmPassword("");
            setSuccess("");
          }, 2000);
        }
      } else {
        setError(data.detail || "Помилка авторизації");
      }
    } catch (err) {
      setError("Помилка з'єднання з сервером");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const inputFocusHandlers = {
    onFocus: (e) => {
      e.target.style.borderColor = palette.indigo;
    },
    onBlur: (e) => {
      e.target.style.borderColor = palette.paperLine;
    },
  };

  return (
    <div style={{ ...styles.container, ...styles.authWrapper }}>
      <GlobalStyle />
      <div style={styles.authCard}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "16px" }}>
            <BrandMark size={34} />
          </div>
          <h2 style={{ fontSize: "27px", fontWeight: "600", color: palette.ink, marginBottom: "8px", fontFamily: fontSerif }}>
            Емоційний щоденник
          </h2>
          <p style={{ color: palette.inkFaint, fontSize: "15px" }}>
            {isLogin ? "Увійдіть до свого акаунту" : "Створіть новий акаунт"}
          </p>
        </div>

        <input
          type="text"
          style={styles.input}
          placeholder="Ім'я користувача"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          {...inputFocusHandlers}
        />
        <input
          type="password"
          style={styles.input}
          placeholder="Пароль"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
          {...inputFocusHandlers}
        />
        {!isLogin && (
          <input
            type="password"
            style={styles.input}
            placeholder="Підтвердіть пароль"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            {...inputFocusHandlers}
          />
        )}

        <button
          style={{ ...styles.button, opacity: loading ? 0.7 : 1 }}
          onClick={handleSubmit}
          disabled={loading}
          onMouseEnter={(e) => { if (!loading) { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = "0 14px 30px rgba(224,122,63,0.45)"; } }}
          onMouseLeave={(e) => { e.target.style.transform = "translateY(0)"; e.target.style.boxShadow = "0 10px 24px rgba(224,122,63,0.38)"; }}
        >
          {loading ? "Завантаження…" : isLogin ? "Увійти" : "Зареєструватися"}
        </button>

        <button
          style={styles.secondaryButton}
          onClick={() => { setIsLogin(!isLogin); setError(""); setSuccess(""); }}
          onMouseEnter={(e) => { e.target.style.background = "rgba(255,255,255,0.7)"; }}
          onMouseLeave={(e) => { e.target.style.background = "rgba(255,255,255,0.4)"; }}
        >
          {isLogin ? "Створити новий акаунт" : "Вже є акаунт? Увійти"}
        </button>

        {error && <div style={styles.errorMessage}>{error}</div>}
        {success && <div style={styles.successMessage}>{success}</div>}
      </div>
    </div>
  );
}


// ========================
// Header
// ========================
function Header({ currentPage, setCurrentPage, username, onLogout }) {
  const [loggingOut, setLoggingOut] = useState(false);

  const pages = [
    { key: "dashboard", label: "Огляд" },
    { key: "new", label: "Новий запис" },
    { key: "entries", label: "Мої записи" },
    { key: "stats", label: "Статистика" },
  ];

  return (
    <div style={styles.header}>
      <div style={styles.headerContent}>
        <div style={styles.logoRow}>
          <BrandMark size={22} />
          <div style={styles.logo}>Емоційний щоденник</div>
        </div>
        <div style={styles.nav}>
          {pages.map((page) => (
            <button
              key={page.key}
              style={{
                ...styles.navButton,
                ...(currentPage === page.key ? styles.navButtonActive : {}),
              }}
              onClick={() => setCurrentPage(page.key)}
            >
              {page.label}
            </button>
          ))}
          <button style={styles.headerCta} onClick={() => setCurrentPage("new")}>
            Новий запис
          </button>
          <span style={{ marginLeft: "10px", fontSize: "13.5px", color: palette.inkFaint }}>{username}</span>
          <button
            style={{ ...styles.navButton, marginLeft: "0px" }}
            onClick={async () => {
              setLoggingOut(true);
              await onLogout();
            }}
            disabled={loggingOut}
          >
            {loggingOut ? "Вихід…" : "Вийти"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ========================
// Dashboard Page
// ========================
function DashboardPage({ token, setCurrentPage }) {
  const [dashboard, setDashboard] = useState(null);
  const [emotionalIndex, setEmotionalIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const authHeaders = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API_URL}/diary/dashboard`, { headers: authHeaders }).then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      }),
      fetch(`${API_URL}/diary/index/emotional`, { headers: authHeaders }).then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      }),
    ])
      .then(([dashboardData, indexData]) => {
        setDashboard(dashboardData);
        setEmotionalIndex(indexData);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h2 style={styles.title}>Завантаження…</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...styles.card, textAlign: "center", padding: "40px", borderLeft: `4px solid ${palette.wine}` }}>
        <h3 style={{ color: palette.wine, fontFamily: fontSerif }}>Помилка</h3>
        <p style={{ color: palette.inkSoft }}>{error}</p>
      </div>
    );
  }

  const weekIndex = dashboard?.week_index ?? 0;
  const monthIndex = dashboard?.month_index ?? 0;
  const weekMood = getIndexLabel(weekIndex);
  const monthMood = getIndexLabel(monthIndex);

  return (
    <div>
      <div style={styles.dashboardHero}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "24px", flexWrap: "wrap", alignItems: "center" }}>
          <div>
            <h2 style={{ ...styles.title, color: palette.paper, marginBottom: "10px" }}>Огляд</h2>
            <p style={{ color: "rgba(241,234,217,0.8)", lineHeight: "1.7", margin: 0 }}>
              Короткий підсумок ваших записів, емоційної динаміки та активності щоденника.
            </p>
          </div>
          <button
            style={{ ...styles.compactButton, minWidth: "180px" }}
            onClick={() => setCurrentPage("new")}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
          >
            Додати запис
          </button>
        </div>
      </div>

      <div style={styles.dashboardGrid}>
        <div style={{ ...styles.statCard, position: "relative" }}>
          <span title={getValenceTooltip(weekIndex, "Цього тижня")} aria-label="Пояснення індексу валентності" style={styles.infoTag}>i</span>
          <div style={{ ...styles.statLabel, marginTop: "4px" }}>Цього тижня</div>
          <div style={{ fontSize: "20px", fontWeight: "700", color: getIndexColor(weekIndex), marginTop: "10px", marginBottom: "6px", fontFamily: fontSerif }}>
            {weekMood}
          </div>
          <p style={{ color: palette.inkFaint, lineHeight: "1.6", marginBottom: 0, fontSize: "14px" }}>{getIndexDescription(weekIndex)}</p>
        </div>
        <div style={{ ...styles.statCard, position: "relative" }}>
          <span title={getValenceTooltip(monthIndex, "Цього місяця")} aria-label="Пояснення індексу валентності" style={styles.infoTag}>i</span>
          <div style={{ ...styles.statLabel, marginTop: "4px" }}>Цього місяця</div>
          <div style={{ fontSize: "20px", fontWeight: "700", color: getIndexColor(monthIndex), marginTop: "10px", marginBottom: "6px", fontFamily: fontSerif }}>
            {monthMood}
          </div>
          <p style={{ color: palette.inkFaint, lineHeight: "1.6", marginBottom: 0, fontSize: "14px" }}>{getIndexDescription(monthIndex)}</p>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{dashboard?.streak_days || 0}</div>
          <div style={styles.statLabel}>Найбільша серія днів</div>
          <p style={{ color: palette.inkFaint, marginBottom: 0, marginTop: "8px", fontSize: "14px" }}>Регулярні записи роблять динаміку помітнішою</p>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{dashboard?.total_entries || 0}</div>
          <div style={styles.statLabel}>Записів у щоденнику</div>
          <p style={{ color: palette.inkFaint, marginBottom: 0, marginTop: "8px", fontSize: "14px" }}>сьогодні: {dashboard?.today_entries || 0}</p>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={{ color: palette.ink, marginTop: 0, fontFamily: fontSerif, fontWeight: "600" }}>Поточні емоції</h3>
        <div style={{ display: "flex", gap: "28px", flexWrap: "wrap" }}>
          <p style={{ marginBottom: 0 }}><strong>Сьогодні:</strong>{" "}{dashboard?.today_emotion ? <Mood emotion={dashboard.today_emotion} /> : "ще немає запису"}</p>
          <p style={{ marginBottom: 0 }}><strong>Тиждень:</strong>{" "}{dashboard?.week_emotion ? <Mood emotion={dashboard.week_emotion} /> : "—"}</p>
          <p style={{ marginBottom: 0 }}><strong>Місяць:</strong>{" "}{dashboard?.month_emotion ? <Mood emotion={dashboard.month_emotion} /> : "—"}</p>
        </div>
      </div>

      {dashboard?.recent?.length > 0 && (
        <div style={{ ...styles.card, marginTop: "24px" }}>
          <h3 style={{ color: palette.ink, marginTop: 0, fontFamily: fontSerif, fontWeight: "600" }}>Останні записи</h3>
          {dashboard.recent.map((entry, index) => (
            <div key={`${entry.date}-${index}`} style={{ padding: "14px 0", borderBottom: index === dashboard.recent.length - 1 ? "none" : `1px solid ${palette.paperLine}` }}>
              <Mood emotion={entry.emotion} size="badge" />
              <span style={{ color: palette.inkFaint, marginLeft: "10px", fontSize: "13px" }}>{entry.date}</span>
              <p style={{ color: palette.inkSoft, marginBottom: 0, marginTop: "8px" }}>{entry.text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ========================
// New Entry Form
// ========================
function NewEntryForm({ token }) {
  const today = new Date().toISOString().split("T")[0];
  const [text, setText] = useState("");
  const [date, setDate] = useState(today);
  const [tags, setTags] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const addPrompt = (prompt) => {
    setText((current) => current ? `${current}\n\n${prompt} ` : `${prompt} `);
    setSuccess("");
    setError("");
  };

  const saveEntry = async () => {
    if (!text.trim()) {
      setError("Напишіть хоча б кілька речень перед аналізом.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const analyzeRes = await fetch(`${API_URL}/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });

      if (!analyzeRes.ok) throw new Error("Помилка аналізу тексту");

      const analysis = await analyzeRes.json();
      setResult(analysis);

      const saveRes = await fetch(`${API_URL}/diary`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          text,
          date,
          emotion: analysis["визначена_емоція"],
          confidence: analysis["ймовірність"],
          tags,
        }),
      });

      if (!saveRes.ok) throw new Error("Помилка збереження запису");

      setText("");
      setTags("");
      setSuccess("Запис збережено. Його вже можна знайти у розділі «Мої записи».");
      setTimeout(() => setResult(null), 60000);
      setTimeout(() => setSuccess(""), 60000);
    } catch (err) {
      setError(err.message || "Щось пішло не так");
      console.error("Помилка:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.grid}>
      <div style={{ ...styles.card, ...styles.infoCard }}>
        <div style={{ marginBottom: "22px" }}>
          <BrandMark size={32} color={palette.paper} />
        </div>
        <h2 style={{ fontSize: "30px", fontWeight: "600", marginBottom: "18px", fontFamily: fontSerif }}>
          Емоційний щоденник
        </h2>
        <p style={{ fontSize: "16px", lineHeight: "1.7", marginBottom: "26px", color: "rgba(241,234,217,0.85)" }}>
          Записуйте свої думки та емоції щодня. Аналіз допоможе відстежувати
          ваш психоемоційний стан у динаміці.
        </p>
        <div>
          <span style={styles.chip}>AI-аналіз</span>
          <span style={styles.chip}>Щоденні записи</span>
          <span style={styles.chip}>Статистика</span>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={{ fontSize: "22px", fontWeight: "600", marginBottom: "18px", color: palette.ink, fontFamily: fontSerif }}>
          Новий запис
        </h3>
        <div style={styles.promptGrid}>
          {writingPrompts.map((prompt) => (
            <button
              key={prompt}
              type="button"
              style={styles.promptButton}
              onClick={() => addPrompt(prompt)}
            >
              {prompt}
            </button>
          ))}
        </div>
        <p style={{ ...styles.helperText, marginBottom: "12px" }}>
          Підказки можна натискати кілька разів — вони допомагають швидше почати рефлексію.
        </p>
        <textarea
          style={styles.textarea}
          placeholder="Опишіть ваш день, думки та почуття..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={(e) => { e.target.style.borderColor = palette.indigo; }}
          onBlur={(e) => { e.target.style.borderColor = palette.paperLine; }}
        />
        <div style={{ ...styles.helperText, display: "flex", justifyContent: "space-between", gap: "12px" }}>
          <span>{text.trim().split(/\s+/).filter(Boolean).length} слів</span>
          <span>{text.length} символів</span>
        </div>
        <input
          type="date"
          style={styles.input}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <input
          type="text"
          style={styles.input}
          placeholder="Теги через кому: робота, сімʼя, навчання..."
          value={tags}
          onChange={(e) => setTags(e.target.value)}
        />
        <p style={styles.helperText}>
          Теги допоможуть пізніше швидше знаходити записи за контекстом.
        </p>
        <button
          style={{
            ...styles.button,
            opacity: loading || !text.trim() ? 0.6 : 1,
            cursor: loading || !text.trim() ? "not-allowed" : "pointer",
          }}
          onClick={saveEntry}
          disabled={loading || !text.trim()}
          onMouseEnter={(e) => { if (!loading && text.trim()) { e.target.style.transform = "translateY(-2px)"; e.target.style.boxShadow = "0 14px 30px rgba(224,122,63,0.45)"; } }}
          onMouseLeave={(e) => { e.target.style.transform = "translateY(0)"; e.target.style.boxShadow = "0 10px 24px rgba(224,122,63,0.38)"; }}
        >
          {loading ? "Аналізую…" : "Зберегти запис"}
        </button>

        {error && <div style={styles.errorMessage}>{error}</div>}
        {success && <div style={styles.successMessage}>{success}</div>}

        {result && (
          <div style={styles.result}>
            <h4 style={{ fontSize: "18px", marginBottom: "12px", fontWeight: "600", fontFamily: fontSerif }}>
              Результат аналізу
            </h4>
            <p style={{ fontSize: "16px", marginBottom: "6px" }}>
              <strong>Емоція:</strong> {result["визначена_емоція"]}
            </p>
            <p style={{ fontSize: "16px" }}>
              <strong>Впевненість:</strong> {(result["ймовірність"] * 100).toFixed(1)}%
            </p>
            <div style={{ ...styles.progressTrack, background: "rgba(241,234,217,0.25)" }}>
              <div
                style={{
                  ...styles.progressFill,
                  background: palette.brass,
                  width: `${normalizeConfidence(result["ймовірність"]) * 100}%`,
                }}
              />
            </div>
            {Array.isArray(result.top_3) && result.top_3.length > 0 && (
              <div style={{ marginTop: "16px" }}>
                <strong>Top-3 варіанти:</strong>
                {result.top_3.map((item) => (
                  <div key={item["емоція"]} style={{ marginTop: "8px" }}>
                    {item["емоція"]} — {(normalizeConfidence(item["ймовірність"]) * 100).toFixed(1)}%
                  </div>
                ))}
              </div>
            )}
            {result["пояснення"] && (
              <p style={{ marginTop: "14px", lineHeight: "1.6", color: "rgba(241,234,217,0.85)" }}>
                {result["пояснення"]}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


// ========================
// Entries List
// ========================
function EntriesList({ token }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [emotionFilter, setEmotionFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ text: "", date: "", tags: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchEntries = () => {
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/diary`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setEntries(Array.isArray(data) ? data : data.entries || []);
      })
      .catch((err) => {
        console.error("Помилка завантаження:", err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleDelete = async (entryId) => {
    if (!window.confirm("Видалити цей запис?")) return;
    setDeletingId(entryId);
    try {
      const res = await fetch(`${API_URL}/diary/${entryId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok || res.status === 204) {
        setEntries((prev) => prev.filter((e) => e.id !== entryId));
      } else {
        alert("Не вдалося видалити запис");
      }
    } catch (err) {
      console.error("Помилка видалення:", err);
    } finally {
      setDeletingId(null);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditDraft({
      text: entry.text || "",
      date: entry.date || new Date().toISOString().split("T")[0],
      tags: entry.tags || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({ text: "", date: "", tags: "" });
  };

  const saveEdit = async (entryId) => {
    if (!editDraft.text.trim()) {
      alert("Текст запису не може бути порожнім");
      return;
    }

    setSavingEdit(true);
    try {
      const analyzeRes = await fetch(`${API_URL}/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: editDraft.text }),
      });
      if (!analyzeRes.ok) throw new Error("Не вдалося повторно проаналізувати текст");
      const analysis = await analyzeRes.json();

      const res = await fetch(`${API_URL}/diary/${entryId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          text: editDraft.text,
          date: editDraft.date,
          tags: editDraft.tags,
          emotion: analysis["визначена_емоція"],
          confidence: analysis["ймовірність"],
        }),
      });

      if (!res.ok) throw new Error("Не вдалося зберегти зміни");
      const updatedEntry = await res.json();
      setEntries((prev) => prev.map((entry) => entry.id === entryId ? updatedEntry : entry));
      cancelEdit();
    } catch (err) {
      alert(err.message || "Помилка редагування запису");
    } finally {
      setSavingEdit(false);
    }
  };

  const uniqueEmotions = Array.from(
    new Set(entries.map((entry) => entry.emotion).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const filteredEntries = entries
    .filter((entry) => {
      const normalizedSearch = searchTerm.trim().toLowerCase();
      const matchesText = `${entry.text || ""} ${entry.tags || ""}`.toLowerCase().includes(normalizedSearch);
      const matchesEmotion = emotionFilter === "all" || entry.emotion === emotionFilter;
      return matchesText && matchesEmotion;
    })
    .sort((a, b) => {
      const dateCompare = String(b.date || "").localeCompare(String(a.date || ""));
      return sortOrder === "newest" ? dateCompare : -dateCompare;
    });

  const groupedByDate = filteredEntries.reduce((acc, entry) => {
    const dateKey = entry.date || "Без дати";
    acc[dateKey] = acc[dateKey] || [];
    acc[dateKey].push(entry);
    return acc;
  }, {});

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h2 style={styles.title}>Завантаження…</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...styles.card, textAlign: "center", padding: "40px", borderLeft: `4px solid ${palette.wine}` }}>
        <h3 style={{ color: palette.wine, marginBottom: "10px", fontFamily: fontSerif }}>Помилка завантаження</h3>
        <p style={{ color: palette.inkSoft }}>{error}</p>
        <button
          style={{ ...styles.button, maxWidth: "200px", margin: "20px auto 0" }}
          onClick={fetchEntries}
        >
          Спробувати знову
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 style={styles.title}>
        Мої записи ({filteredEntries.length} із {entries.length})
      </h2>
      <div style={styles.filterPanel}>
        <label style={{ fontWeight: "600", color: palette.inkSoft, fontSize: "14px" }}>
          Пошук
          <input
            style={{ ...styles.select, marginTop: "8px" }}
            type="search"
            placeholder="Знайти слово або фразу..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </label>
        <label style={{ fontWeight: "600", color: palette.inkSoft, fontSize: "14px" }}>
          Емоція
          <select
            style={{ ...styles.select, marginTop: "8px" }}
            value={emotionFilter}
            onChange={(e) => setEmotionFilter(e.target.value)}
          >
            <option value="all">Усі емоції</option>
            {uniqueEmotions.map((emotion) => (
              <option key={emotion} value={emotion}>
                {emotion}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontWeight: "600", color: palette.inkSoft, fontSize: "14px" }}>
          Сортування
          <select
            style={{ ...styles.select, marginTop: "8px" }}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="newest">Спочатку нові</option>
            <option value="oldest">Спочатку старі</option>
          </select>
        </label>
        <button
          style={{ ...styles.compactButton, opacity: filteredEntries.length ? 1 : 0.55 }}
          onClick={() => downloadCsv(filteredEntries)}
          disabled={!filteredEntries.length}
        >
          Експорт CSV
        </button>
      </div>
      {Object.keys(groupedByDate).length === 0 ? (
        <div style={{ ...styles.card, textAlign: "center", padding: "60px 30px" }}>
          <h3 style={{ fontSize: "20px", color: palette.inkFaint, fontFamily: fontSerif, fontWeight: "600" }}>
            {entries.length ? "Нічого не знайдено" : "Записів поки немає"}
          </h3>
          <p style={{ color: palette.inkFaint, marginTop: "10px" }}>
            {entries.length ? "Спробуйте змінити пошук або фільтр емоцій." : "Почніть вести щоденник прямо зараз."}
          </p>
        </div>
      ) : (
        Object.entries(groupedByDate)
          .sort(([dateA], [dateB]) => sortOrder === "newest" ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB))
          .map(([entryDate, dayEntries]) => (
            <div key={entryDate}>
              <div style={styles.dateHeader}>{entryDate}</div>
              {dayEntries.map((entry) => (
                <div
                  key={entry.id}
                  style={styles.entryCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-3px)";
                    e.currentTarget.style.boxShadow = "0 16px 34px rgba(59,50,39,0.14)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 8px 22px rgba(59,50,39,0.08)";
                  }}
                >
                  <button
                    style={styles.deleteButton}
                    onClick={() => handleDelete(entry.id)}
                    disabled={deletingId === entry.id}
                  >
                    {deletingId === entry.id ? "…" : "Видалити"}
                  </button>
                  <button
                    style={{
                      ...styles.deleteButton,
                      right: "104px",
                      color: palette.indigo,
                      borderColor: palette.indigo,
                    }}
                    onClick={() => startEdit(entry)}
                    disabled={editingId === entry.id || savingEdit}
                  >
                    Редагувати
                  </button>
                  <div style={{ marginBottom: "14px" }}>
                    <Mood emotion={entry.emotion} size="badge" />
                  </div>
                  {editingId === entry.id ? (
                    <div>
                      <textarea
                        style={{ ...styles.textarea, minHeight: "140px" }}
                        value={editDraft.text}
                        onChange={(e) => setEditDraft((draft) => ({ ...draft, text: e.target.value }))}
                      />
                      <input
                        type="date"
                        style={styles.input}
                        value={editDraft.date}
                        onChange={(e) => setEditDraft((draft) => ({ ...draft, date: e.target.value }))}
                      />
                      <input
                        type="text"
                        style={styles.input}
                        placeholder="Теги через кому"
                        value={editDraft.tags}
                        onChange={(e) => setEditDraft((draft) => ({ ...draft, tags: e.target.value }))}
                      />
                      <div style={{ display: "flex", gap: "12px", marginTop: "14px", flexWrap: "wrap" }}>
                        <button
                          style={{ ...styles.compactButton, flex: "1", minWidth: "160px", opacity: savingEdit ? 0.6 : 1 }}
                          onClick={() => saveEdit(entry.id)}
                          disabled={savingEdit}
                        >
                          {savingEdit ? "Збереження…" : "Зберегти"}
                        </button>
                        <button
                          style={{
                            ...styles.compactButton,
                            flex: "1",
                            minWidth: "160px",
                            background: "transparent",
                            color: palette.inkSoft,
                            border: `1px solid ${palette.paperLine}`,
                          }}
                          onClick={cancelEdit}
                          disabled={savingEdit}
                        >
                          Скасувати
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: "15.5px", lineHeight: "1.7", marginBottom: "15px", color: palette.ink, paddingRight: "100px" }}>
                        {entry.text}
                      </p>
                      {parseTags(entry.tags).length > 0 && (
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "14px" }}>
                          {parseTags(entry.tags).map((tag) => (
                            <span
                              key={tag}
                              style={{
                                padding: "5px 11px",
                                borderRadius: "999px",
                                background: palette.paperDeep,
                                color: palette.inkSoft,
                                fontSize: "12.5px",
                                fontWeight: "600",
                                border: `1px solid ${palette.paperLine}`,
                              }}
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: "15px", fontSize: "13.5px", color: palette.inkFaint, flexWrap: "wrap" }}>
                        <span>
                          <strong>Впевненість:</strong>{" "}
                          {((entry.confidence || 0) * 100).toFixed(1)}%
                        </span>
                      </div>
                      <div style={styles.progressTrack}>
                        <div
                          style={{
                            ...styles.progressFill,
                            background: emotionColors[entry.emotion] || emotionFallbackColor,
                            width: `${normalizeConfidence(entry.confidence) * 100}%`,
                          }}
                        />
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ))
      )}
    </div>
  );
}


// ========================
// Statistics Page
// ========================
function StatsPage({ token }) {
  const [stats, setStats] = useState(null);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [emotionalIndex, setEmotionalIndex] = useState(null);
  const [tagAnalytics, setTagAnalytics] = useState(null);
  const [moodCalendar, setMoodCalendar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const authHeaders = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch(`${API_URL}/diary/stats/emotions`, { headers: authHeaders }).then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      }),
      fetch(`${API_URL}/diary/report/weekly`, { headers: authHeaders }).then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      }),
      fetch(`${API_URL}/diary/calendar`, { headers: authHeaders }).then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      }),
      fetch(`${API_URL}/diary/report/monthly`, { headers: authHeaders }).then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      }),
      fetch(`${API_URL}/diary/index/emotional`, { headers: authHeaders }).then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      }),
      fetch(`${API_URL}/diary/analytics/tags`, { headers: authHeaders }).then((res) => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.json();
      }),
    ])
      .then(([statsData, weeklyData, calendarData, monthlyData, indexData, tagData]) => {
        setStats(statsData);
        setWeeklyReport(weeklyData);
        setMoodCalendar(Array.isArray(calendarData) ? calendarData : []);
        setMonthlyReport(monthlyData);
        setEmotionalIndex(indexData);
        setTagAnalytics(tagData);
      })
      .catch((err) => {
        console.error("Помилка завантаження статистики:", err);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "50px" }}>
        <h2 style={styles.title}>Завантаження статистики…</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...styles.card, textAlign: "center", padding: "40px", borderLeft: `4px solid ${palette.wine}` }}>
        <h3 style={{ color: palette.wine, fontFamily: fontSerif }}>Помилка</h3>
        <p style={{ color: palette.inkSoft }}>{error}</p>
      </div>
    );
  }

  if (!stats || stats.total_entries === 0) {
    return (
      <div style={{ ...styles.card, textAlign: "center", padding: "60px 30px" }}>
        <h3 style={{ fontSize: "20px", color: palette.inkFaint, fontFamily: fontSerif, fontWeight: "600" }}>Недостатньо даних</h3>
        <p style={{ color: palette.inkFaint, marginTop: "10px" }}>
          Додайте записи до щоденника, щоб побачити статистику
        </p>
      </div>
    );
  }

  const maxCount = Math.max(...Object.values(stats.emotion_counts));

  // Sort emotions by count descending
  const sortedEmotions = Object.entries(stats.emotion_counts).sort(
    ([, a], [, b]) => b - a
  );

  // Find dominant emotion
  const dominantEmotion = sortedEmotions[0]?.[0] || "—";
  const confidencePercent = normalizeConfidence(stats.average_confidence) * 100;
  const recentCount = stats.recent_emotions?.length || 0;
  const dominantCount = sortedEmotions[0]?.[1] || 0;
  const dominantShare = stats.total_entries ? Math.round((dominantCount / stats.total_entries) * 100) : 0;
  const wellbeingTip = (() => {
    if (["смуток", "злість", "страх", "відраза"].includes(dominantEmotion)) {
      return "Останні записи часто мають складні емоції. Спробуйте додати короткий план підтримки: сон, прогулянка, розмова або пауза від перевантаження.";
    }
    if (["радість", "любов"].includes(dominantEmotion)) {
      return "Позитивні емоції добре повторювати: занотуйте, які події або люди найчастіше їх викликають.";
    }
    return "Нейтральний фон теж важливий: додайте більше деталей про контекст дня, щоб модель і статистика стали точнішими.";
  })();

  return (
    <div>
      <h2 style={styles.title}>
        Статистика емоцій
      </h2>

      {/* Summary cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "18px",
          marginBottom: "28px",
        }}
      >
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{stats.total_entries}</div>
          <div style={styles.statLabel}>Всього записів</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ marginTop: "4px" }}>
            <Mood emotion={dominantEmotion} size="badge" />
          </div>
          <div style={{ ...styles.statLabel, marginTop: "12px" }}>Домінантна емоція</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>
            {(stats.average_confidence * 100).toFixed(0)}%
          </div>
          <div style={styles.statLabel}>Середня впевненість</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{Object.keys(stats.emotion_counts).length}</div>
          <div style={styles.statLabel}>Різних емоцій</div>
        </div>
      </div>

      {weeklyReport && (
        <div style={{ ...styles.card, marginBottom: "26px" }}>
          <h3 style={{ fontSize: "20px", fontWeight: "600", color: palette.ink, marginBottom: "14px", fontFamily: fontSerif }}>
            Щотижневий звіт
          </h3>
          <p style={{ color: palette.inkSoft, lineHeight: "1.7", marginBottom: "18px" }}>
            {weeklyReport.summary}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "14px" }}>
            <div style={{ padding: "14px", borderRadius: "10px", background: palette.paperDeep }}>
              <strong>Період</strong>
              <div style={{ color: palette.inkFaint, marginTop: "6px" }}>
                {weeklyReport.period?.from} — {weeklyReport.period?.to}
              </div>
            </div>
            <div style={{ padding: "14px", borderRadius: "10px", background: palette.paperDeep }}>
              <strong>Активні дні</strong>
              <div style={{ color: palette.inkFaint, marginTop: "6px" }}>{weeklyReport.active_days}</div>
            </div>
            <div style={{ padding: "14px", borderRadius: "10px", background: palette.paperDeep }}>
              <strong>Домінантна емоція</strong>
              <div style={{ marginTop: "8px" }}>
                {weeklyReport.dominant_emotion ? <Mood emotion={weeklyReport.dominant_emotion} /> : "—"}
              </div>
            </div>
          </div>
        </div>
      )}

      {emotionalIndex && (
        <div style={{ ...styles.card, marginBottom: "26px" }}>
          <h3 style={{ fontSize: "20px", fontWeight: "600", color: palette.ink, marginBottom: "18px", fontFamily: fontSerif }}>
            Емоційний фон
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
            <div style={{ padding: "18px", borderRadius: "12px", background: palette.paperDeep, textAlign: "left", position: "relative" }}>
              <span title={getValenceTooltip(emotionalIndex.overall_index, "За весь період")} aria-label="Пояснення індексу валентності" style={styles.infoTag}>i</span>
              <div style={{ ...styles.statLabel, marginBottom: "8px" }}>За весь період</div>
              <div style={{ fontSize: "22px", fontWeight: "700", color: getIndexColor(emotionalIndex.overall_index), marginBottom: "8px", fontFamily: fontSerif }}>
                {getIndexLabel(emotionalIndex.overall_index)}
              </div>
              <p style={{ color: palette.inkFaint, margin: 0, fontSize: "14px" }}>{getIndexDescription(emotionalIndex.overall_index)}</p>
            </div>
            <div style={{ padding: "18px", borderRadius: "12px", background: palette.paperDeep, textAlign: "left", position: "relative" }}>
              <span title={getValenceTooltip(emotionalIndex.last_30_days_index, "Останні 30 днів")} aria-label="Пояснення індексу валентності" style={styles.infoTag}>i</span>
              <div style={{ ...styles.statLabel, marginBottom: "8px" }}>Останні 30 днів</div>
              <div style={{ fontSize: "22px", fontWeight: "700", color: getIndexColor(emotionalIndex.last_30_days_index), marginBottom: "8px", fontFamily: fontSerif }}>
                {getIndexLabel(emotionalIndex.last_30_days_index)}
              </div>
              <p style={{ color: palette.inkFaint, margin: 0, fontSize: "14px" }}>{getIndexDescription(emotionalIndex.last_30_days_index)}</p>
            </div>
          </div>
        </div>
      )}

      {monthlyReport && (
        <div style={{ ...styles.card, marginBottom: "26px" }}>
          <h3 style={{ fontSize: "20px", fontWeight: "600", color: palette.ink, marginBottom: "14px", fontFamily: fontSerif }}>
            Місячний звіт
          </h3>
          <p style={{ color: palette.inkSoft, lineHeight: "1.7", marginBottom: "18px" }}>
            {monthlyReport.summary}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "14px" }}>
            <div style={{ padding: "14px", borderRadius: "10px", background: palette.paperDeep }}>
              <strong>Активні дні</strong>
              <div style={{ color: palette.inkFaint, marginTop: "6px" }}>{monthlyReport.active_days}</div>
            </div>
            <div style={{ padding: "14px", borderRadius: "10px", background: palette.paperDeep }}>
              <strong>Індекс місяця</strong>
              <div style={{ color: getIndexColor(monthlyReport.emotional_index), marginTop: "6px", fontWeight: "700" }}>
                {monthlyReport.emotional_index}
              </div>
            </div>
            <div style={{ padding: "14px", borderRadius: "10px", background: palette.paperDeep }}>
              <strong>Найкращий день</strong>
              <div style={{ color: palette.inkFaint, marginTop: "6px" }}>
                {monthlyReport.best_day ? `${monthlyReport.best_day.date} (${monthlyReport.best_day.index})` : "—"}
              </div>
            </div>
            <div style={{ padding: "14px", borderRadius: "10px", background: palette.paperDeep }}>
              <strong>Найскладніший день</strong>
              <div style={{ color: palette.inkFaint, marginTop: "6px" }}>
                {monthlyReport.worst_day ? `${monthlyReport.worst_day.date} (${monthlyReport.worst_day.index})` : "—"}
              </div>
            </div>
          </div>
        </div>
      )}

      {tagAnalytics && (tagAnalytics.tags?.length > 0 || tagAnalytics.possible_triggers?.length > 0) && (
        <div style={{ ...styles.card, marginBottom: "26px" }}>
          <h3 style={{ fontSize: "20px", fontWeight: "600", color: palette.ink, marginBottom: "18px", fontFamily: fontSerif }}>
            Аналітика тегів і можливих тригерів
          </h3>
          {tagAnalytics.possible_triggers?.length > 0 && (
            <div style={{ marginBottom: "18px" }}>
              <strong style={{ color: palette.ink }}>Можливі емоційні тригери:</strong>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: "12px", marginTop: "12px" }}>
                {tagAnalytics.possible_triggers.slice(0, 4).map((trigger) => (
                  <div key={trigger.tag} style={{ padding: "14px", borderRadius: "10px", background: "rgba(123,59,70,0.06)", border: `1px solid rgba(123,59,70,0.18)` }}>
                    <strong>#{trigger.tag}</strong>
                    <div style={{ color: palette.inkFaint, marginTop: "6px" }}>
                      <Mood emotion={trigger.possible_trigger_emotion} /> · {trigger.entries_count} запис(ів)
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {tagAnalytics.tags?.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "12px" }}>
              {tagAnalytics.tags.slice(0, 8).map((tag) => (
                <div key={tag.tag} style={{ padding: "14px", borderRadius: "10px", background: palette.paperDeep }}>
                  <strong>#{tag.tag}</strong>
                  <div style={{ color: palette.inkFaint, marginTop: "6px" }}>
                    {tag.count} запис(ів) · <Mood emotion={tag.dominant_emotion} />
                  </div>
                  <div style={{ color: getIndexColor(tag.emotional_index), fontWeight: "700", marginTop: "6px" }}>
                    індекс: {tag.emotional_index}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {Array.isArray(stats.top_3_emotions) && stats.top_3_emotions.length > 0 && (
        <div style={{ ...styles.card, marginBottom: "26px" }}>
          <h3 style={{ fontSize: "20px", fontWeight: "600", color: palette.ink, marginBottom: "18px", fontFamily: fontSerif }}>
            Top-3 емоції за весь період
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "14px" }}>
            {stats.top_3_emotions.map((item, index) => (
              <div
                key={item.emotion}
                style={{
                  padding: "18px",
                  borderRadius: "12px",
                  background: `${emotionColors[item.emotion] || emotionFallbackColor}14`,
                  border: `1px solid ${emotionColors[item.emotion] || emotionFallbackColor}40`,
                }}
              >
                <div style={{ fontSize: "22px", fontWeight: "700", marginBottom: "8px", fontFamily: fontSerif, color: palette.inkFaint }}>
                  #{index + 1}
                </div>
                <Mood emotion={item.emotion} size="badge" />
                <div style={{ color: palette.inkFaint, marginTop: "10px" }}>
                  {item.count} запис(ів), {(normalizeConfidence(item.share) * 100).toFixed(0)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {moodCalendar.length > 0 && (
        <div style={{ ...styles.card, marginBottom: "26px" }}>
          <h3 style={{ fontSize: "20px", fontWeight: "600", color: palette.ink, marginBottom: "6px", fontFamily: fontSerif }}>
            Дні за домінантною емоцією
          </h3>
          <p style={{ ...styles.helperText, marginTop: 0, marginBottom: "18px" }}>
            Емоція, яка переважала того дня, якщо записів було декілька.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", gap: "12px" }}>
            {moodCalendar.slice(0, 35).map((day) => (
              <div
                key={day.date}
                style={{
                  padding: "14px",
                  borderRadius: "10px",
                  background: `${emotionColors[day.dominant_emotion] || emotionFallbackColor}18`,
                  border: `1px solid ${emotionColors[day.dominant_emotion] || emotionFallbackColor}40`,
                  textAlign: "center",
                }}
              >
                <div style={{ fontWeight: "700", color: palette.ink }}>{day.date}</div>
                <div style={{ marginTop: "8px" }}>
                  <Mood emotion={day.dominant_emotion} />
                </div>
                <div style={{ fontSize: "12px", color: palette.inkFaint, marginTop: "6px" }}>
                  {day.entries_count} запис(ів)
                </div>
              </div>
            ))}
          </div>
          {moodCalendar.length > 35 && (
            <p style={styles.helperText}>Показано останні 35 днів із записами.</p>
          )}
        </div>
      )}

      <div style={styles.insightGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Інсайт</div>
          <p style={{ color: palette.inkSoft, lineHeight: "1.6", marginBottom: 0, marginTop: "10px" }}>
            Емоція «{dominantEmotion}» займає приблизно {dominantShare}% усіх записів.
          </p>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Якість аналізу</div>
          <p style={{ color: palette.inkSoft, lineHeight: "1.6", marginBottom: 0, marginTop: "10px" }}>
            Середня впевненість моделі — {confidencePercent.toFixed(0)}%. Якщо вона низька, пиши конкретніше: подія → думка → емоція.
          </p>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Рекомендація</div>
          <p style={{ color: palette.inkSoft, lineHeight: "1.6", marginBottom: 0, marginTop: "10px" }}>
            {wellbeingTip}
          </p>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Динаміка</div>
          <p style={{ color: palette.inkSoft, lineHeight: "1.6", marginBottom: 0, marginTop: "10px" }}>
            У швидкій стрічці показано {recentCount} останніх записів. Регулярні записи роблять тренди більш надійними.
          </p>
        </div>
      </div>

      {/* Emotion distribution */}
      <div style={{ ...styles.card, marginBottom: "26px" }}>
        <h3 style={{ fontSize: "20px", fontWeight: "600", color: palette.ink, marginBottom: "22px", fontFamily: fontSerif }}>
          Розподіл емоцій
        </h3>
        {sortedEmotions.map(([emotion, count]) => (
          <div key={emotion} style={styles.emotionBar}>
            <span style={{ minWidth: "160px", fontSize: "14.5px", fontWeight: "600", color: palette.inkSoft, display: "flex", alignItems: "center" }}>
              <span style={{ ...styles.moodDot, background: emotionColors[emotion] || emotionFallbackColor }} />
              {emotion}
            </span>
            <div
              style={{
                ...styles.emotionBarFill,
                background: emotionColors[emotion] || emotionFallbackColor,
                width: `${(count / maxCount) * 100}%`,
              }}
            >
              {count}
            </div>
          </div>
        ))}
      </div>

      {/* Recent emotions timeline */}
      {stats.recent_emotions && stats.recent_emotions.length > 0 && (
        <div style={styles.card}>
          <h3 style={{ fontSize: "20px", fontWeight: "600", color: palette.ink, marginBottom: "22px", fontFamily: fontSerif }}>
            Останні записи
          </h3>
          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
            {stats.recent_emotions.map((item, idx) => (
              <div
                key={idx}
                style={{
                  background: palette.paperDeep,
                  borderRadius: "10px",
                  padding: "16px",
                  textAlign: "center",
                  minWidth: "120px",
                  flex: "1",
                  border: `1px solid ${palette.paperLine}`,
                }}
              >
                <span style={{ ...styles.moodDot, marginRight: 0, background: emotionColors[item.emotion] || emotionFallbackColor }} />
                <div style={{ fontSize: "13px", fontWeight: "600", color: palette.inkSoft, marginTop: "8px" }}>
                  {item.emotion}
                </div>
                <div style={{ fontSize: "12px", color: palette.inkFaint, marginTop: "4px" }}>
                  {item.date}
                </div>
                <div style={{ fontSize: "11px", color: palette.inkFaint, marginTop: "2px" }}>
                  {((item.confidence || 0) * 100).toFixed(0)}%
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ========================
// Main App
// ========================
export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [token, setToken] = useState("");
  const [currentPage, setCurrentPage] = useState("dashboard");

  // Restore session from localStorage
  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedUsername = localStorage.getItem("username");
    if (savedToken && savedUsername) {
      setToken(savedToken);
      setUsername(savedUsername);
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (user, authToken) => {
    setUsername(user);
    setToken(authToken);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (err) {
      console.error("Помилка при logout:", err);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("username");
      setUsername("");
      setToken("");
      setIsAuthenticated(false);
      setCurrentPage("dashboard");
    }
  };

  if (!isAuthenticated) {
    return <AuthPage onLogin={handleLogin} />;
  }

  const renderPage = () => {
    switch (currentPage) {
      case "dashboard":
        return <DashboardPage token={token} setCurrentPage={setCurrentPage} />;
      case "entries":
        return <EntriesList token={token} />;
      case "stats":
        return <StatsPage token={token} />;
      default:
        return <NewEntryForm token={token} />;
    }
  };

  return (
    <div style={styles.container}>
      <GlobalStyle />
      <Header
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        username={username}
        onLogout={handleLogout}
      />
      <div style={styles.content}>{renderPage()}</div>
    </div>
  );
}